import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// ── AM I AN ADMIN? ───────────────────────────────────────────────────────────
// Read from the ID TOKEN's custom claims, never from a Firestore field. The
// claim is signed by Firebase and set only by the Admin SDK (see
// functions/adminClaims.js), so nothing the client does can influence it.
//
// `force` DECIDES WHETHER A NETWORK ROUND TRIP HAPPENS, and the two callers
// want opposite things:
//
//   · AdminRoute passes `true`. Claims are baked into the token when it is
//     issued and the SDK reuses that token for about an hour, so a freshly
//     promoted admin would keep being refused at the door for up to an hour —
//     which reads exactly like the promotion having failed. One round trip at
//     the boundary is worth more than that confusion.
//
//   · UserMenu passes `false`. It renders in the header of EVERY page, for
//     every signed-in user, admin or not — forcing a refresh there would be a
//     network call per page load for everyone, to decide whether to draw one
//     menu item. The cached token answers it for free. The cost is that a
//     just-promoted admin does not see the menu entry until their token next
//     refreshes; they can still reach /admin directly, and AdminRoute's forced
//     check lets them in, after which the cached token carries the claim and
//     the entry appears.
//
// THIS IS A CONVENIENCE, NOT A CONTROL, at either setting. Everything it
// guards is also enforced server-side: firestore.rules checks
// `request.auth.token.admin`, and every admin callable re-checks it. If this
// hook were bypassed entirely, the reader would reach a page that could not
// load a single number.
export function useAdmin({ force = true } = {}) {
  const { user, loading: authLoading } = useAuth();
  // `null` = not yet known, and it is deliberately distinct from `false`.
  // Rendering "access denied" while the answer is still in flight is how a
  // legitimate admin gets bounced off their own dashboard on every reload.
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    let alive = true;
    if (authLoading) return undefined;
    if (!user) { setIsAdmin(false); return undefined; }

    setIsAdmin(null);
    user.getIdTokenResult(force)
      .then((res) => { if (alive) setIsAdmin(res.claims?.admin === true); })
      // A failed refresh must not read as "you are an admin".
      .catch(() => { if (alive) setIsAdmin(false); });

    return () => { alive = false; };
  }, [user, authLoading, force]);

  return { isAdmin, checking: authLoading || isAdmin === null };
}

export default useAdmin;
